import { api } from './api'

function assertSuccess(body: { success?: boolean; message?: string; data?: unknown }) {
  if (!body?.success) {
    throw new Error(body?.message || 'Request failed')
  }
  return body.data
}

export type PaymentMethodSummary = {
  present: boolean
  method: string | null
  label: string | null
  cardLast4: string | null
  cardExpiry: string | null
  consumerAccount: string | null
}

export type MerchantSubscription = {
  planCode: string
  status: string
  billingInterval: 'monthly' | 'yearly'
  trialEndsAt: string | null
  currentPeriodEndsAt: string | null
  graceEndsAt: string | null
  billingRequired: boolean
  complimentary?: boolean
  eligibleTrial: boolean
  planName: string | null
  planAmount: number | null
  currency: string
  displayPrice: string | null
  maxStaffCount: number | null
  staffCount: number
  staffSeatsRemaining: number | null
  paymentMethod?: PaymentMethodSummary
  billingCurrency?: string | null
  billingProvider?: 'stripe' | 'paystack' | string | null
  billingLocked?: boolean
  pendingBillingCurrency?: 'EUR' | 'NGN' | string
  pendingBillingProvider?: 'stripe' | 'paystack' | string
  contactCountry?: string | null
  contactCountryCode?: string | null
}

export async function fetchSubscription(): Promise<MerchantSubscription | null> {
  const { data } = await api.get('/storebusiness/subscription')
  return assertSuccess(data) as MerchantSubscription | null
}

export async function startBillingSetup(options?: {
  updatePaymentMethod?: boolean
}): Promise<{
  authorizationUrl: string
  paymentId: string
  provider?: 'stripe' | 'paystack'
  currency?: 'EUR' | 'NGN'
}> {
  const { data } = await api.post('/storebusiness/billing/setup', {
    updatePaymentMethod: options?.updatePaymentMethod === true,
  })
  return assertSuccess(data) as {
    authorizationUrl: string
    paymentId: string
    provider?: 'stripe' | 'paystack'
    currency?: 'EUR' | 'NGN'
  }
}

export type BillingSyncResult = {
  subscription: MerchantSubscription | null
  applied: boolean
  paymentStatus: string
  message: string
}

export async function syncBillingPayment(): Promise<BillingSyncResult> {
  const { data } = await api.post('/storebusiness/billing/sync')
  return assertSuccess(data) as BillingSyncResult
}

export async function updateOrganisationCountry(input: {
  contactCountry: string
  contactCountryCode: string
}): Promise<{
  contactCountry: string
  contactCountryCode: string
  billingLocked: boolean
  pendingBillingCurrency: 'EUR' | 'NGN'
  pendingBillingProvider: 'stripe' | 'paystack'
}> {
  const { data } = await api.patch('/storebusiness/organisation/country', input)
  return assertSuccess(data) as {
    contactCountry: string
    contactCountryCode: string
    billingLocked: boolean
    pendingBillingCurrency: 'EUR' | 'NGN'
    pendingBillingProvider: 'stripe' | 'paystack'
  }
}
